import React, { FC, memo, useState, useEffect, useRef } from "react";
import { IPrice, IImage, IGLSParcel, IShippingAgentServiceCode } from "@montana/models/generated/cms.interface";
import { Media } from "@montana/shared/media";
import { Price } from "@montana/shared/price";
import { FormSelect, TextInput } from "@montana/shared/form";
import { useTranslations } from "@montana/containers/translations";
import { ParcelShopsList } from "../parcel-shops-list";
import { useCheckout } from "@montana/containers/checkout";
import { getParcelShops } from "@montana/services/checkout";
import { Translate } from "@montana/shared/translate";
import { IFieldOptionValue } from "@montana/shared/form/field/field";
import { useConnectedServiceFunctions } from "@montana/shared/use-connected-service-functions";
import {useSettings} from '@montana/containers/settings/settings.container';

export type ShippingType = 'parcelShop' | 'homeDelivery';

export interface IShippingOption {
    type: ShippingType;
    info?: string;
    price?: IPrice;
    dispatchTime?: string;
    image?: IImage;
    homeDeliveryOptions?: IShippingAgentServiceCode[];
    zipCode?: number;
    deliveryId?: string;
}

interface IShippingOptionProps {
    option: IShippingOption;
    isSelected: boolean;
    onSelectParcelShop: (parcelShopId: string) => void;
}

export const ShippingOption: FC<IShippingOptionProps> = memo(({ option, isSelected, onSelectParcelShop }: IShippingOptionProps) => {
    const translate = useTranslations();
    const {market} = useSettings();
    const [parcelShops, setParcelShops] = useState<IGLSParcel[]>([]);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [isSearching, setIsSearching] = useState<boolean>(false);
    const abortController = useRef<AbortController | undefined>();
    const debounceTime = 500;

    const [cGetParcelShops] = useConnectedServiceFunctions(getParcelShops);

    const {
        customerInformation
    } = useCheckout();

    useEffect(() => {
        if (customerInformation.shippingAddress && market?.marketId) {
            cGetParcelShops(customerInformation.shippingAddress.zipCode, market.marketId)
                .then(response => {
                    if (response.ok) {
                        response.json().then(parcelShops => setParcelShops(parcelShops))
                    } else {
                        console.error('Failed to get parcel shops')
                    }
                })
                .catch(error => console.error('Failed to get parcel shops', error));
        }
    }, []);

    useEffect(() => {
        if (!searchQuery) {
            setIsSearching(false);
            return;
        }

        setIsSearching(true);
        abortLatestSearch();
        const debounceTimeOut = setTimeout(() => {
            searchByQuery()
        }, debounceTime);

        return () => {
            clearTimeout(debounceTimeOut);
        }
    }, [searchQuery]);

    const abortLatestSearch = () => abortController.current && abortController.current.abort();

    const searchByQuery = async () => {
        try {
            const controller = new AbortController();
            abortController.current = controller;

            const response: Response = await cGetParcelShops(searchQuery, market?.marketId ?? '');

            if (response.ok) {
                const searchResult: IGLSParcel[] = await response.json();
                setParcelShops(searchResult);
            } else {
                setParcelShops([]);
                console.error(`Search parcel shops request failed with ${response.status}, ${response.statusText}`);
            }
        } catch (error) {
            setParcelShops([]);
            console.error(error);
        } finally {
            setIsSearching(false);
        }
    }
    const handleParcelShopChange = (shopId: string) => {
        onSelectParcelShop(shopId);
    }

    return (
        <>
            <div className="shipping-option__row-1">
                {option.info}
                {option.image && <Media className="shipping-option__image" image={option.image} />}
            </div>
            <div>
                +<Price obj={option.price} />.
                &nbsp;
                {option.dispatchTime}
            </div>
            <div className="shipping-option__features">
                {option.type === 'homeDelivery' && option.homeDeliveryOptions && option.homeDeliveryOptions.length > 0 &&
                    <div className="shipping-option__features-input">
                        <FormSelect
                            name="homeDeliveryOption"
                            defaultValue={option.homeDeliveryOptions[0].key}
                            options={option.homeDeliveryOptions?.map(o => ({ value: o.key, text: o.displayName } as IFieldOptionValue))}
                        />
                    </div>
                }
                {option.type === 'parcelShop' &&
                    <div className="shipping-option__features-input">
                        <TextInput
                            type="text"
                            placeholder={translate(t => t.checkoutPage.shippingInformationZipCode, '[Zip code]')}
                            value={option.zipCode}
                            onChange={e => setSearchQuery(e.target.value)}
                        />
                        {!isSearching && isSelected && <ParcelShopsList shops={parcelShops} onChange={handleParcelShopChange} />}
                        {isSearching && <Translate value="general.generalSearching" defaultValue="[Searching...]" />}
                        <div className="field-wrapper__error no-parcel-shop-selected-error">{translate(t => t.forms.mustSelectValue, "[An option must be selected]")}</div>
                    </div>
                }
            </div>
        </>
    );
});
